import { useEffect, useRef, useState } from 'react'
import type { ChartTimeframe } from '../types'
import { displaySymbol } from '../utils/displaySymbol'
import { useSurfaceTheme } from '../theme/surfaceTheme'
import { CHART_THEMES } from './dashboard/chartTheme'

export const TRADINGVIEW_SCRIPT_URL = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js'

const INTERVALS: Record<ChartTimeframe, string> = { '1m': '1', '5m': '5', '1h': '60', '1d': 'D' }

/**
 * "BTC/USDT" -> "BINANCE:BTCUSDT". Our own prices come from Kraken, but
 * TradingView lists far more USDT pairs under Binance, so every coin in the
 * ticker universe resolves.
 */
// Binance lists MATIC only under its new ticker, POL.
export const tradingViewSymbol = (symbol: string) => `BINANCE:${displaySymbol(symbol).replace('/', '').toUpperCase()}`

const localTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Etc/UTC'
  } catch {
    return 'Etc/UTC'
  }
}

interface TradingViewChartProps {
  symbol: string
  timeframe: ChartTimeframe
}

/**
 * TradingView's free Advanced Chart widget for one coin, themed to match the
 * Dashboard's card and gridlines (dark or light). It shows TradingView's own price feed, which can differ
 * slightly from LivePriceChart's exchange prices.
 */
const TradingViewChart = ({ symbol, timeframe }: TradingViewChartProps) => {
  const hostRef = useRef<HTMLDivElement>(null)
  const [failed, setFailed] = useState(false)
  const tvSymbol = tradingViewSymbol(symbol)
  const interval = INTERVALS[timeframe]
  const theme = useSurfaceTheme()

  useEffect(() => {
    const host = hostRef.current
    if (!host) return
    setFailed(false)

    // The embed script reads its config from its own body and puts an iframe
    // beside it, so a coin change rebuilds the widget from scratch.
    const widget = document.createElement('div')
    widget.className = 'tradingview-widget-container__widget'
    widget.style.height = '100%'
    widget.style.width = '100%'

    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.src = TRADINGVIEW_SCRIPT_URL
    script.async = true
    script.onerror = () => setFailed(true)
    script.innerHTML = JSON.stringify({
      autosize: true,
      symbol: tvSymbol,
      interval,
      timezone: localTimeZone(),
      theme,
      style: '1',
      locale: 'en',
      backgroundColor: CHART_THEMES[theme].surface,
      gridColor: CHART_THEMES[theme].grid,
      // The Dashboard's coin selector drives the symbol.
      allow_symbol_change: false,
      calendar: false,
      support_host: 'https://www.tradingview.com',
    })

    // Each build gets its own wrapper, and cleanup detaches the wrapper instead
    // of emptying it. An embed script from a superseded build (React
    // StrictMode's double effect in development, or a quick coin change) can
    // still run after cleanup; it looks up its parent node, which must exist.
    //
    // The wrapper must carry TradingView's container class: the script swaps
    // its iframe into that element. Without the class it appends a second
    // full-height box after the empty widget div, leaving a blank gap above
    // the chart.
    const wrapper = document.createElement('div')
    wrapper.className = 'tradingview-widget-container'
    wrapper.style.height = '100%'
    wrapper.style.width = '100%'
    wrapper.appendChild(widget)
    wrapper.appendChild(script)
    host.appendChild(wrapper)
    return () => {
      script.onerror = null
      wrapper.remove()
    }
  }, [tvSymbol, interval, theme])

  return (
    <div className="flex h-full flex-col">
      {failed && (
        <div className="flex flex-1 items-center justify-center text-center text-sm text-gray-400">
          TradingView couldn't load. Switch to Simple for FinCreator's own live prices.
        </div>
      )}
      <div
        ref={hostRef}
        className={`min-h-0 flex-1 ${failed ? 'hidden' : ''}`}
        data-testid="tradingview-widget"
        data-symbol={tvSymbol}
        data-interval={interval}
      />
      <p className="mt-2 text-right text-xs text-gray-500">
        <a
          href={`https://www.tradingview.com/symbols/${tvSymbol.split(':')[1]}/?exchange=BINANCE`}
          rel="noopener nofollow noreferrer"
          target="_blank"
          className="hover:text-gray-300"
        >
          {displaySymbol(symbol)} chart by TradingView
        </a>
      </p>
    </div>
  )
}

export default TradingViewChart
